// @flow
import * as React from 'react';
import styled from 'styled-components';
import Box from 'components/Box';
import { AnimatedCheckmark } from 'components/Icon';
import { Body } from 'components/Text';
import TabContext from 'containers/TabContext';
import { colors, transition } from '../theme';

const Input = styled.input`
  width: 100%;
  height: 100%;
  margin: 0;
  appearance: none;
  cursor: pointer;
  ${props =>
    !props.isTabbing &&
    `
    &:focus {
      outline: 0;
    }
  `}
  ${props =>
    props.hideRadioButtons &&
    `
    border: 0;
    background: transparent;
    border-radius: 0;
  `}
`;

const CheckmarkContainer = styled.div.attrs(({ checked }) => ({
  style: {
    background: checked ? 'white' : 'rgba(255, 255, 255, 0)',
    color: checked ? 'white' : colors.charcoalGrey,
    boxShadow: `0 0 0 1px ${checked ? 'white' : 'rgba(0,0,0,0.5)'}`
  }
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transition: all ${transition};
`;

const Container = styled.div.attrs(({ checked }) => ({
  style: {
    background: checked ? colors.waterBlue : colors.platinum,
    color: checked ? 'white' : colors.charcoalGrey
  }
}))`
  position: relative;
  display: flex;
  align-items: center;
  padding: 20px 24px;
  height: 100%;
  border-radius: 10px;
  transition: all ${transition};
  &:hover {
    background: ${colors.waterBlue} !important;
    color: white !important;
    ${CheckmarkContainer} {
      box-shadow: 0 0 0 1px white !important;
    }
  }
`;

const LargeRadioButton = ({
  id,
  name,
  value,
  label,
  checked,
  onChange,
  hideRadioButtons,
  ...props
}: {
  id?: string,
  name?: string,
  value: string | boolean | number,
  label: React.Node,
  checked: boolean,
  onChange: (string | boolean | number) => void,
  hideRadioButtons?: boolean
}) => (
  <Container checked={checked}>
    <Box top={0} left={0} position="absolute" width={1} height="100%">
      <TabContext.Consumer>
        {isTabbing => (
          <Input
            type="radio"
            name={name}
            id={id}
            checked={checked}
            value={typeof value === 'string' ? value : value.toString()}
            onChange={e => {
              e.stopPropagation();
              onChange(value);
            }}
            isTabbing={isTabbing}
            hideRadioButtons={hideRadioButtons}
            {...props}
          />
        )}
      </TabContext.Consumer>
    </Box>
    {!hideRadioButtons && (
      <CheckmarkContainer checked={checked}>
        {checked && (
          <AnimatedCheckmark
            width="8px"
            height="8px"
            stroke={colors.waterBlue}
            delay={0}
          />
        )}
      </CheckmarkContainer>
    )}
    <Box htmlFor={id} as="label" ml={16}>
      <Body>{label}</Body>
    </Box>
  </Container>
);

export default LargeRadioButton;
